import React, { useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Popover } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { colors } from 'src/components/app-ui'
import { GoogleFontList } from 'src/utils/googleFontList'

// Some entries of the list have no family name; they can't be picked.
const FAMILIES = Array.from(
  new Set(GoogleFontList.items.map(item => item.family).filter(Boolean)),
).sort((a, b) => a.localeCompare(b))

// Rendered in pages: the next page is added as the list scrolls to its end.
const PAGE_SIZE = 100

/**
 * Loads one Google Font for previews. A failed load only drops the preview;
 * nothing is thrown (font-picker-react threw "Response has status code 0"
 * whenever its Google Fonts API request failed).
 */
export const loadFont = family => {
  const id = `branding-font-${family.replace(/\s+/g, '-')}`
  if (!family || document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family,
  )}&display=swap`
  document.head.appendChild(link)
}

const useStyles = makeStyles({
  trigger: {
    width: '100%',
    maxWidth: 280,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '0 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.navy,
    '&:focus': {
      outline: 'none',
      borderColor: colors.primary,
    },
  },
  paper: {
    width: 280,
    marginTop: 4,
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    boxShadow:
      '0 20px 40px -4px rgba(145,158,171,.28), 0 0 2px rgba(145,158,171,.2)',
    overflow: 'hidden',
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderBottom: `1px solid ${colors.border}`,
    color: colors.subtle,
  },
  searchInput: {
    flex: 1,
    border: 0,
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
  },
  list: {
    maxHeight: 280,
    overflowY: 'auto',
    padding: 4,
  },
  option: {
    width: '100%',
    display: 'block',
    padding: '8px 10px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '14px',
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
  },
  empty: {
    padding: '12px 10px',
    fontSize: '13px',
    color: colors.muted,
  },
})

/**
 * Searchable font dropdown (Google Fonts list bundled with the admin).
 * Without a value it shows `placeholder` (e.g. "Not set").
 */
const FontSelect = ({ value, onChange, placeholder }) => {
  const classes = useStyles()
  const triggerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [limit, setLimit] = useState(PAGE_SIZE)

  useEffect(() => {
    if (value) loadFont(value)
  }, [value])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q
      ? FAMILIES.filter(family => family.toLowerCase().includes(q))
      : FAMILIES
  }, [query])
  const results = matches.slice(0, limit)

  const handleScroll = event => {
    const list = event.currentTarget
    if (
      limit < matches.length &&
      list.scrollTop + list.clientHeight >= list.scrollHeight - 80
    ) {
      setLimit(current => current + PAGE_SIZE)
    }
  }

  const pick = family => {
    setOpen(false)
    setQuery('')
    setLimit(PAGE_SIZE)
    if (family !== value) onChange(family)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={classes.trigger}
        style={
          value
            ? { fontFamily: `'${value}', sans-serif` }
            : { color: colors.subtle }
        }
        onClick={() => setOpen(true)}
      >
        {value || placeholder}
        <Icon
          icon="tabler:chevron-down"
          width={16}
          height={16}
          color={colors.muted}
        />
      </button>
      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.paper, elevation: 0 }}
      >
        <label className={classes.search}>
          <Icon icon="tabler:search" width={14} height={14} />
          <input
            className={classes.searchInput}
            autoFocus
            value={query}
            placeholder="Search fonts..."
            onChange={event => {
              setQuery(event.target.value)
              setLimit(PAGE_SIZE)
            }}
          />
        </label>
        <div className={classes.list} onScroll={handleScroll}>
          {results.map(family => (
            <button
              key={family}
              type="button"
              className={classes.option}
              style={
                family === value
                  ? { color: colors.primary, fontWeight: 600 }
                  : undefined
              }
              onClick={() => pick(family)}
            >
              {family}
            </button>
          ))}
          {results.length === 0 && (
            <div className={classes.empty}>No fonts match your search.</div>
          )}
        </div>
      </Popover>
    </>
  )
}

export default FontSelect
